import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  fetchCalendarConfig,
  fetchCalendarEvents,
  fetchCalendarProviders,
  syncCalendar,
} from "./api/calendarApi";
import CalendarConnectPanel from "./connect/CalendarConnectPanel";
import CalendarEventDialog from "./views/CalendarEventDialog";
import {
  DEFAULT_SHOP_TIMEZONE,
  eventShopHour,
  formatUtcIsoAsShopLocalTime,
  loadSavedCalendarView,
  saveCalendarView,
  shiftShopView,
  shopHourSlots,
  shopLocalDateKeyFromIso,
  shopLocalDateKeyFromMoment,
  shopLocalTodayKey,
  shopMonthMatrix,
  shopTimezoneOrDefault,
  shopViewLabel,
  shopWeekDays,
  visibleShopGridRange,
} from "./utils/calendarTime";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function isDayClosed(dayHours) {
  if (!dayHours || dayHours.closed) {
    return true;
  }
  return !dayHours.open || !dayHours.close;
}

/**
 * Portable shop calendar (TASK-522).
 * Parts 0–3, 6–7: shop TZ, details/reschedule/cancel, no free create, month/week/day + cache.
 */
export default function MechanicsCalendar({
  shopId,
  userId = null,
  compact = false,
  isOwner = false,
}) {
  const [viewDate, setViewDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState(() =>
    compact ? "month" : loadSavedCalendarView(userId, shopId),
  );
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState(null);
  const [connection, setConnection] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const autoSyncedForShop = useRef(null);

  const shopTz = shopTimezoneOrDefault(config?.timezone);

  useEffect(() => {
    if (compact) {
      setViewMode("month");
      return;
    }
    setViewMode(loadSavedCalendarView(userId, shopId));
  }, [userId, shopId, compact]);

  const handleViewMode = (_e, next) => {
    if (!next) return;
    setViewMode(next);
    saveCalendarView(userId, shopId, next);
  };

  const load = useCallback(async () => {
    if (!shopId) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const configRes = await fetchCalendarConfig(shopId);
      setConfig(configRes.config);
      setConnection(configRes.connection);
      const tz = shopTimezoneOrDefault(configRes.config?.timezone);
      const range = visibleShopGridRange(viewDate, tz);

      if (
        isOwner &&
        configRes.connection?.connection_status === "connected" &&
        autoSyncedForShop.current !== shopId
      ) {
        autoSyncedForShop.current = shopId;
        try {
          await syncCalendar(shopId);
        } catch (syncErr) {
          console.warn("Calendar auto-sync on open failed:", syncErr);
        }
      }

      const [eventsRes, providersRes] = await Promise.all([
        fetchCalendarEvents(shopId, range.from, range.to),
        fetchCalendarProviders(),
      ]);
      setEvents(eventsRes.events || []);
      setProviders(providersRes.providers || []);
    } catch (err) {
      console.error("MechanicsCalendar load error:", err);
      setError(err.message || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [shopId, viewDate, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  const monthCells = useMemo(() => shopMonthMatrix(viewDate, shopTz), [viewDate, shopTz]);
  const weekDays = useMemo(() => shopWeekDays(viewDate, shopTz), [viewDate, shopTz]);
  const hourSlots = useMemo(() => shopHourSlots(6, 20), []);
  const todayKey = shopLocalTodayKey(shopTz);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const event of events) {
      const key = shopLocalDateKeyFromIso(event.start_at, shopTz);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    }
    return map;
  }, [events, shopTz]);

  const formatTime = (iso) => formatUtcIsoAsShopLocalTime(iso, shopTz);
  const headerLabel = shopViewLabel(viewDate, viewMode, shopTz);

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const renderEventChip = (event) => (
    <Typography
      key={event.id}
      variant="caption"
      onClick={(e) => {
        e.stopPropagation();
        if (!compact) openEditDialog(event);
      }}
      sx={{
        display: "block",
        bgcolor: "primary.light",
        color: "primary.contrastText",
        borderRadius: 0.5,
        px: 0.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "0.65rem",
        cursor: compact ? "default" : "pointer",
        mb: 0.25,
      }}
      title={`${event.title} (${formatTime(event.start_at)} – ${formatTime(event.end_at)})`}
    >
      {formatTime(event.start_at)} {event.title}
    </Typography>
  );

  if (!shopId) {
    return <Alert severity="warning">Shop context is required for the calendar.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={compact ? 0 : 1} sx={{ p: compact ? 0 : 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            aria-label="Previous"
            onClick={() => setViewDate(shiftShopView(viewDate, viewMode, -1, shopTz))}
          >
            <ChevronLeftIcon />
          </Button>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, minWidth: 160, textAlign: "center", whiteSpace: "nowrap" }}
          >
            {headerLabel}
          </Typography>
          <Button
            size="small"
            aria-label="Next"
            onClick={() => setViewDate(shiftShopView(viewDate, viewMode, 1, shopTz))}
          >
            <ChevronRightIcon />
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {!compact && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={handleViewMode}
              aria-label="Calendar view"
            >
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="day">Day</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Chip
            size="small"
            label={`Shop time: ${config?.timezone || DEFAULT_SHOP_TIMEZONE}`}
            variant="outlined"
          />
          {!compact && isOwner && (
            <IconButton
              size="small"
              aria-label="Calendar settings"
              onClick={() => setSettingsOpen(true)}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {viewMode === "month" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0.5,
            minHeight: compact ? undefined : 420,
          }}
        >
          {WEEKDAY_LABELS.map((label) => (
            <Typography
              key={label}
              variant="caption"
              sx={{ fontWeight: 700, textAlign: "center", py: 0.5 }}
            >
              {label}
            </Typography>
          ))}
          {monthCells.map((day) => {
            const dayEvents = eventsByDay.get(day.dateKey) || [];
            const isToday = day.dateKey === todayKey;
            const dayHours = config?.business_hours?.[WEEKDAY_KEYS[day.weekdayIndex]];
            const closed = isDayClosed(dayHours);
            return (
              <Box
                key={day.dateKey}
                sx={{
                  minHeight: compact ? 72 : 96,
                  border: "1px solid",
                  borderColor: isToday ? "primary.main" : "divider",
                  borderRadius: 1,
                  p: 0.5,
                  bgcolor: closed
                    ? "action.hover"
                    : day.inMonth
                      ? "background.paper"
                      : "action.hover",
                  opacity: day.inMonth ? (closed ? 0.55 : 1) : closed ? 0.55 : 0.72,
                }}
              >
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 500 }}>
                    {day.dayOfMonth}
                  </Typography>
                  {closed && (
                    <Typography variant="caption" color="text.secondary">
                      Closed
                    </Typography>
                  )}
                </Stack>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {dayEvents.slice(0, compact ? 2 : 3).map(renderEventChip)}
                  {dayEvents.length > (compact ? 2 : 3) && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayEvents.length - (compact ? 2 : 3)} more
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {viewMode === "week" && !compact && (
        <Box sx={{ overflowX: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "56px repeat(7, minmax(100px, 1fr))",
              gap: 0.5,
              minWidth: 720,
            }}
          >
            <Box />
            {weekDays.map((d) => (
              <Typography
                key={d.dateKey}
                variant="caption"
                sx={{
                  fontWeight: d.dateKey === todayKey ? 700 : 600,
                  textAlign: "center",
                  color: d.dateKey === todayKey ? "primary.main" : "text.primary",
                }}
              >
                {d.label}
              </Typography>
            ))}
            {hourSlots.map((slot) => (
              <React.Fragment key={`h-${slot.hour}`}>
                <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                  {slot.label}
                </Typography>
                {weekDays.map((d) => {
                  const dayEvents = (eventsByDay.get(d.dateKey) || []).filter(
                    (ev) => eventShopHour(ev.start_at, shopTz) === slot.hour,
                  );
                  return (
                    <Box
                      key={`${d.dateKey}-${slot.hour}`}
                      sx={{
                        minHeight: 44,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 0.5,
                        p: 0.25,
                      }}
                    >
                      {dayEvents.map(renderEventChip)}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}

      {viewMode === "day" && !compact && (
        <Box>
          {(() => {
            const dayKey = shopLocalDateKeyFromMoment(viewDate, shopTz);
            const dayEvents = eventsByDay.get(dayKey) || [];
            return (
              <Stack spacing={0.5}>
                {hourSlots.map((slot) => {
                  const slotEvents = dayEvents.filter(
                    (ev) => eventShopHour(ev.start_at, shopTz) === slot.hour,
                  );
                  return (
                    <Box
                      key={`day-${slot.hour}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "64px 1fr",
                        gap: 1,
                        minHeight: 48,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {slot.label}
                      </Typography>
                      <Box>{slotEvents.map(renderEventChip)}</Box>
                    </Box>
                  );
                })}
                {dayEvents.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No appointments this day (shop time).
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </Box>
      )}

      {/* Compact embed always uses month cells above */}

      <CalendarEventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        shopId={shopId}
        event={selectedEvent}
        timeZone={shopTz}
        onSaved={load}
      />

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Calendar settings</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shop timezone, booking rules, and shop management system connection.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`Timezone: ${config?.timezone || "—"}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Connection: ${connection?.connection_status || "disconnected"}`}
              color={
                connection?.connection_status === "connected"
                  ? "success"
                  : connection?.connection_status === "unhealthy"
                    ? "warning"
                    : "default"
              }
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Providers: ${providers.length} supported`}
              variant="outlined"
            />
          </Stack>
          <CalendarConnectPanel
            embedded
            shopId={shopId}
            config={config}
            connection={connection}
            providers={providers}
            onSaved={load}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
